import { Component, OnInit } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { AdminViewComponent } from "./admin-view.component";
import { AutoApprovalThresholdDisplayModel, TableName, IEditModalOption } from "src/app/npr-request.model";
import { ApiService } from "src/app/services";
import { ModalService } from "src/app/shared/modal.service";
import { AutoApprovalThresholdColumnDefinitionForAdmin } from "./ag-grid-column-definition";
import { DataTypes } from "./admin-view-data-types";
import { AutoApprovalThreshold } from "../../generated-models";
import { SharedDataService } from "../../services/sharedDataService";

@Component({
  standalone: false,
  selector: "admin-view/execution-plan-auto-approval-threshold",
  templateUrl: "./admin-view.component.html",
  styleUrls: ["../../styles.scss"],
})
export class AdminViewAutoApprovalThresholdComponent extends AdminViewComponent<AutoApprovalThresholdDisplayModel> implements OnInit {
  tableName = TableName.AutoApprovalThreshold;
  columnDef = AutoApprovalThresholdColumnDefinitionForAdmin;
  keyFilterStats = "filter:admin-view-execution-plan-auto-approval-threshold";

  constructor(
    apiService: ApiService,
    modalService: ModalService,
    notificationService: ToastrService,
    dataTypes: DataTypes,
    sharedDataService: SharedDataService
  ) {
    super(apiService, modalService, notificationService, sharedDataService, dataTypes);
    this.type = "Auto Approval Threshold";
    this.addRowEnable = true;
    this.updateRowFunc = async (data: AutoApprovalThresholdDisplayModel) => {
      // Validate Threshold for update operations (only Threshold is editable)
      if (data.Threshold === null || data.Threshold === undefined || data.Threshold <= 0) {
        this.notificationService.error("Threshold must be greater than 0.", "Validation Error");
        throw new Error("Threshold must be greater than 0.");
      }

      const payload: AutoApprovalThreshold = {
        RequestServiceType: data.RequestServiceType,
        Sku: data.Sku,
        Location: data.Location,
        Threshold: data.Threshold,
      };
      return apiService.updateAutoApprovalThreshold(payload).toPromise();
    };
    this.addRowFunc = async (data: AutoApprovalThresholdDisplayModel) => {
      // Validate all four parameters are provided
      const validationError = this.validateAutoApprovalThreshold(data);
      if (validationError) {
        this.notificationService.error(validationError, "Validation Error");
        throw new Error(validationError);
      }

      const payload: AutoApprovalThreshold = {
        RequestServiceType: data.RequestServiceType,
        Sku: data.Sku,
        Location: data.Location,
        Threshold: data.Threshold,
      };
      return apiService.insertAutoApprovalThreshold(payload).toPromise();
    };
    this.deleteRowEnable = true;
    this.deleteRowFunc = (data: AutoApprovalThresholdDisplayModel) => {
      return apiService.deleteAutoApprovalThreshold(data.Location, data.RequestServiceType, data.Sku).toPromise();
    };
  }

  editRow(): void {
    if (!this.editRowEnable) {
      return;
    }
    
    if (!this.selectedRow) {
      this.message = "Please select one row to edit";
      return;
    }

    this.message = "";
    // For AutoApprovalThreshold, only Threshold is editable in edit mode
    const editableList = ["Threshold"];
    
    const data: IEditModalOption[] = this.columnDef.map((col: any) => {
      const key = col.field;
      let value = this.selectedRow[key];
      let type: string = col.valueType || typeof value;
      if (type === "object") {
        type = "string";
        value = "";
      }

      // For edit mode, only Threshold is editable; primary key fields are read-only
      const isEditable = key === "Threshold";
      
      let currentOptions = null;
      if (isEditable) {
        currentOptions = this.dataTypes.getOptions(type);
      }

      return {
        key: key,
        name: col.headerName,
        value: value,
        type: type,
        options: isEditable && currentOptions,
        isEditable: isEditable,
        hide: col.hide || false,
        isNullable: false,
        displayName: null,
      };
    });

    const p = this.modalService.editModal(this.type, data, this.tableName, editableList, this.updateRowFunc);
    p.then((response) => {
      if (response) {
        this.rowData = [];
        this.refreshData();
        console.log("Updated row for table", this.tableName);
      } else {
        console.log("Cancelled to update row for table", this.tableName);
      }
    }).catch((error) => {
      if (error) {
        this.message = error;
      }
    });
  }

  private validateAutoApprovalThreshold(data: AutoApprovalThresholdDisplayModel): string | null {
    if (!data.Location || data.Location.trim() === "") {
      return "Location is required.";
    }
    if (!data.RequestServiceType || data.RequestServiceType.trim() === "") {
      return "Request Service Type is required.";
    }
    if (!data.Sku || data.Sku.trim() === "") {
      return "SKU is required.";
    }
    if (data.Threshold === null || data.Threshold === undefined || data.Threshold <= 0) {
      return "Threshold must be greater than 0.";
    }
    return null;
  }

  async addRow(): Promise<void> {
    if (this.addRowEnable) {
      console.log("add new row for: ", this.tableName);
      this.message = "";
      
      // For add mode, all fields except ID are editable
      const data: IEditModalOption[] = this.columnDef
        .filter((col: any) => !col.hide) // Hide ID field
        .map((property: any) => {
          let type = property.valueType;
          let currentOptions = this.dataTypes.getOptions(type);
          
          // For RequestServiceType field, use executionPlanServiceType options (friendly names)
          if (type === "serviceType") {
            type = "executionPlanServiceType";
            currentOptions = this.dataTypes.getOptions(type);
          }
          
          // Add wildcard support specifically for SKU field in AutoApprovalThreshold
          if (property.valueType === "sku" && currentOptions && Array.isArray(currentOptions)) {
            const skuOptions = currentOptions as { label: string; value: string }[];
            currentOptions = [
              { label: "*", value: "*" },
              ...skuOptions
            ];
          }
          
          const value = "";
          
          return {
            key: property.field,
            name: property.headerName,
            value: value,
            type: type || "string",
            options: currentOptions,
            isEditable: true, // All fields editable in add mode
            hide: property.hide || false,
            isNullable: false,
            displayName: null,
          }
        });

      const p = this.modalService.newRowModal(this.type, data, this.tableName, this.addRowFunc);
      p.then((response) => {
        if (response) {
          this.rowData = [];
          this.refreshData();
          console.log("Add new row for table", this.tableName);
        } else {
          console.log("Cancelled to add new row for table", this.tableName);
        }
      }).catch((error) => {
        if (error) {
          this.message = error;
        }
      });
    }
  }

  refreshData() {
    super.refreshData();
    this.apiService.getAutoApprovalThresholdList().subscribe((response: AutoApprovalThreshold[]) => {
      const data = response.map((p: AutoApprovalThreshold) => {
        const displayModel: AutoApprovalThresholdDisplayModel = {
          RequestServiceType: p.RequestServiceType,
          Sku: p.Sku,
          Location: p.Location,
          Threshold: p.Threshold,
        };
        return displayModel;
      });
      this.setRowData(data);
      super.setupFilterByLocalData();
    });
  }
}
